import { useEffect, useState } from "react";
import "./AddEntityModal.styles.scss";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { DropdownInput } from "@/components/Inputs";
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";
import { AddEntityModalProps } from "./AddEntityModal.types";
import { PORTFOLIO, ACCOUNT } from "../../utils/constants";
import {
  useSavePortfolioMutation,
  useSaveAccountMutation,
} from "../../services/entityManagementApi";
import { useAlerts } from "@/hooks/useAlerts";
import { useAppContext } from "@/context/context";

/**
 * AddEntityModal component
 * Modal for adding new portfolios or accounts
 * Uses RTK Query mutations with automatic cache invalidation
 */
const AddEntityModal = ({
  open,
  entityType,
  selectedPortfolioId,
  setEntityType,
  setSelectedPortfolioId,
  closeModal,
  dataMap,
  refetchAccountsForPortfolio,
}: AddEntityModalProps) => {
  const { showError, showSuccess } = useAlerts();
  const { context_state } = useAppContext();
  const orgId = context_state?.org_metadata?.org_id;

  const [savePortfolio, { isLoading: isSavingPortfolio }] =
    useSavePortfolioMutation();
  const [saveAccount, { isLoading: isSavingAccount }] =
    useSaveAccountMutation();

  const [portfolioName, setPortfolioName] = useState("");
  const [accountName, setAccountName] = useState("");
  const [buttonDisabled, setButtonDisabled] = useState(true);

  // Generate portfolio options from dataMap
  const portfolioOptions: ISelectOption[] = Array.from(dataMap.values()).map(
    (portfolio) => ({
      value: portfolio.portfolio_id,
      label: portfolio.display_name,
    }),
  );

  // Validation
  useEffect(() => {
    if (entityType === PORTFOLIO) {
      const isDuplicate = Array.from(dataMap.values()).some(
        (p) =>
          p.display_name.toLowerCase() === portfolioName.trim().toLowerCase(),
      );
      setButtonDisabled(portfolioName.trim().length === 0 || isDuplicate);
    } else {
      const portfolio = dataMap.get(selectedPortfolioId || "");
      const isDuplicate = portfolio?.accounts?.some(
        (a: any) =>
          a.display_name.toLowerCase() === accountName.trim().toLowerCase(),
      );
      setButtonDisabled(
        accountName.trim().length === 0 ||
          !selectedPortfolioId ||
          isDuplicate ||
          false,
      );
    }
  }, [entityType, portfolioName, accountName, selectedPortfolioId, dataMap]);

  const handleSave = async () => {
    try {
      if (entityType === PORTFOLIO) {
        if (!orgId) {
          showError("Organization ID not found");
          return;
        }
        const payload = {
          orgId,
          data: { display_name: portfolioName.trim() },
        };
        await savePortfolio(payload).unwrap();
        showSuccess("Portfolio created successfully");
      } else {
        if (!selectedPortfolioId) {
          showError("Portfolio not selected");
          return;
        }
        await saveAccount({
          portfolioId: selectedPortfolioId,
          data: { display_name: accountName.trim() },
        }).unwrap();
        showSuccess("Account created successfully");

        // Manually refetch accounts for the portfolio to update the UI
        await refetchAccountsForPortfolio(selectedPortfolioId);
      }
      handleClose();
    } catch (error: any) {
      showError(error?.data?.message || error?.message || "Failed to save");
    }
  };

  const handleClose = () => {
    setPortfolioName("");
    setAccountName("");
    closeModal();
  };

  const handleEntityTypeChange = (value: string) => {
    setPortfolioName("");
    setAccountName("");
    setSelectedPortfolioId(null);
    setEntityType(value as typeof PORTFOLIO | typeof ACCOUNT);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="add-entity-modal-dialog-title"
      aria-describedby="add-entity-modal-dialog-description"
      className="add-entity-modal"
      fullWidth
    >
      <DialogTitle id="add-entity-modal-dialog-title">Add Entity</DialogTitle>
      <DialogContent>
        <DropdownInput
          value={entityType}
          label="Entity Type"
          onChange={(e: any) => handleEntityTypeChange(e.target.value)}
          name="entity-type"
          options={[
            { value: PORTFOLIO, label: "Portfolio" },
            { value: ACCOUNT, label: "Account" },
          ]}
          disabled={false}
          formInput
          omitEmptyOption
          containerClassNames=""
        />

        {entityType === PORTFOLIO ? (
          <>
            <p className="pulse-typography-subtitle-2 pulse-color--background-100 pulse-mt-2 pulse-mb-1 pulse-pl-1">
              New Portfolio Name
            </p>
            <input
              name="portfolio-name"
              className="pulse-ml-1"
              value={portfolioName}
              onChange={(e) => setPortfolioName(e.target.value)}
              placeholder="new portfolio..."
            />
          </>
        ) : (
          <>
            <DropdownInput
              value={selectedPortfolioId || ""}
              label="Portfolio"
              onChange={(e: any) => setSelectedPortfolioId(e.target.value)}
              name="portfolio"
              options={portfolioOptions}
              disabled={false}
              formInput
              omitEmptyOption
              containerClassNames="pulse-mb-1"
            />
            <p className="pulse-typography-subtitle-2 pulse-color--background-100 pulse-mt-2 pulse-mb-1 pulse-pl-1">
              New Account Name
            </p>
            <input
              name="account-name"
              className="pulse-ml-1"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              placeholder="new account..."
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          className="add-entity-modal-cancel-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          className="add-entity-modal-save-button"
          disabled={buttonDisabled || isSavingPortfolio || isSavingAccount}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntityModal;
